import { PlayCircle } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { resolveApiMediaUrl } from '../../services/apiClient';

export function PreviewPlayer({ 
  title = 'Select a track to preview', 
  subtitle = 'No version selected',
  videoUrl,
  onPlaybackStateChange
}: { 
  title?: string; 
  subtitle?: string;
  videoUrl?: string | null;
  onPlaybackStateChange?: (isPlaying: boolean) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const resolvedVideoUrl = resolveApiMediaUrl(videoUrl);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const emptyMessage = subtitle === 'Preview not available' ? subtitle : 'Preview Window';

  useEffect(() => {
    setPlaybackError(null);
    onPlaybackStateChange?.(false);
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [onPlaybackStateChange, resolvedVideoUrl]);

  return (
    <section className='container-shell py-3 sm:py-4'>
      <div className='glass mx-auto max-w-3xl overflow-hidden rounded-2xl'>
        <div className='flex aspect-video items-center justify-center bg-black relative'>
          {resolvedVideoUrl ? (
            <>
              <video
                ref={videoRef}
                src={resolvedVideoUrl}
                className="absolute inset-0 w-full h-full object-contain"
                controls
                controlsList="nodownload"
                autoPlay
                playsInline
                preload="auto"
                onLoadedMetadata={() => setPlaybackError(null)}
                onCanPlay={() => setPlaybackError(null)}
                onPlay={() => onPlaybackStateChange?.(true)}
                onPause={() => onPlaybackStateChange?.(false)}
                onEnded={() => onPlaybackStateChange?.(false)}
                onError={() => {
                  onPlaybackStateChange?.(false);
                  setPlaybackError('Preview could not load');
                }}
              >
                Your browser does not support the video tag.
              </video>
              {playbackError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 px-6 text-center">
                  <PlayCircle className='h-16 w-16 text-prime-red/50' />
                  <p className="mt-4 text-sm font-black uppercase tracking-widest text-white">{playbackError}</p>
                  <p className="mt-2 text-xs text-white/45">Try selecting the preview again.</p>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center gap-4 text-white/20">
              <PlayCircle className='h-20 w-20 text-prime-red/40' />
              <p className="font-bold uppercase tracking-widest text-xs">{emptyMessage}</p>
            </div>
          )}
        </div>
        <div className='border-t border-white/10 bg-white/[0.03] px-4 py-3'>
          <h2 className='text-lg font-black text-prime-red sm:text-xl'>{title}</h2>
          {subtitle ? <p className='mt-1 text-sm text-white/50'>{subtitle}</p> : null}
        </div>
      </div>
    </section>
  );
}
